import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { UserTypeContext, UserIDContext } from "../../App";
import { usePesapal } from "./usePesapal";
import { API, graphqlOperation } from "aws-amplify";
import moment from "moment";

function PesapalCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUserTypeAWS, setUserTypeLocal, setDaysLeft, daysLeft } =
    useContext(UserTypeContext);
  const userID = useContext(UserIDContext);
  const { checkPaymentStatus } = usePesapal();

  const [status, setStatus] = useState("checking"); // checking, success, failed, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  
  // Ref to prevent duplicate processing (React strict mode, re-renders)
  const isProcessingRef = React.useRef(false);
  const processedTrackingIdRef = React.useRef(null);

  // Get order tracking ID from URL params (Pesapal redirects with this)
  const orderTrackingId =
    searchParams.get("OrderTrackingId") || searchParams.get("orderTrackingId");

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get stored payment data from localStorage
        const storedPayment = localStorage.getItem("pesapal_payment");
        let paymentData = storedPayment ? JSON.parse(storedPayment) : null;

        if (!orderTrackingId) {
          // Try to get from stored data
          if (paymentData && paymentData.orderTrackingId) {
            await verifyAndProcessPayment(
              paymentData.orderTrackingId,
              paymentData
            );
          } else {
            setStatus("error");
            setErrorMessage("No payment tracking ID found");
          }
          return;
        }

        await verifyAndProcessPayment(orderTrackingId, paymentData);
      } catch (err) {
        console.error("Callback processing error:", err);
        setStatus("error");
        setErrorMessage(err.message || "Failed to process payment");
      }
    };

    processCallback();
  }, [orderTrackingId]);

  const verifyAndProcessPayment = async (trackingId, paymentData) => {
    try {
      // Check payment status with Pesapal
      const paymentStatus = await checkPaymentStatus(trackingId);
      console.log("Payment status:", paymentStatus);

      setPaymentDetails({
        ...paymentData,
        transactionStatus: paymentStatus,
      });

      // Check if payment was successful
      const statusDescription =
        paymentStatus.payment_status_description?.toUpperCase();

      if (statusDescription === "COMPLETED" || statusDescription === "PAID") {
        // Prevent duplicate processing (React strict mode, polling, re-renders)
        if (processedTrackingIdRef.current === trackingId) {
          console.log("⚠️ Payment already processed for this trackingId, skipping duplicate");
          setStatus("success");
          return;
        }
        
        // Mark as processing to prevent concurrent calls
        if (isProcessingRef.current) {
          console.log("⚠️ Already processing a payment, skipping duplicate call");
          return;
        }
        isProcessingRef.current = true;

        // Process the payment update - updateSubscription will:
        // 1. Fetch current userTypeChangeDate from DB
        // 2. Calculate remaining days (X)
        // 3. Add new subscription duration (X + 30 or X + 365)
        // 4. Update userTypeChangeDate with new expiry
        console.log("Payment successful, processing subscription update...");
        try {
          await updateSubscription(paymentData);
          // Mark this trackingId as processed
          processedTrackingIdRef.current = trackingId;
        } catch (updateErr) {
          console.error("Failed to update subscription:", updateErr);
          setStatus("error");
          setErrorMessage("Payment received but failed to activate subscription. Please contact support.");
          isProcessingRef.current = false;
          return;
        }
        
        isProcessingRef.current = false;
        setStatus("success");

        // Clear stored payment data
        localStorage.removeItem("pesapal_payment");
      } else if (statusDescription === "PENDING") {
        setStatus("checking");
        setErrorMessage("Payment is still being processed. Please wait...");

        // Poll again after 5 seconds
        setTimeout(() => {
          verifyAndProcessPayment(trackingId, paymentData);
        }, 5000);
      } else if (
        statusDescription === "FAILED" ||
        statusDescription === "INVALID"
      ) {
        setStatus("failed");
        setErrorMessage("Payment was not successful. Please try again.");
        localStorage.removeItem("pesapal_payment");
      } else {
        // Unknown status - show pending
        setStatus("checking");
        setErrorMessage(
          `Payment status: ${statusDescription || "Unknown"}. Checking...`
        );

        // Poll again after 5 seconds
        setTimeout(() => {
          verifyAndProcessPayment(trackingId, paymentData);
        }, 5000);
      }
    } catch (err) {
      console.error("Payment verification error:", err);
      setStatus("error");
      setErrorMessage("Failed to verify payment status");
    }
  };

  const updateSubscription = async (paymentData) => {
    const { packageType, duration, userId } = paymentData;
    
    // Determine effective userId - prefer localStorage value, fallback to context
    const effectiveUserId = userId || userID;
    console.log("=== updateSubscription DEBUG ===");
    console.log("paymentData.userId:", userId);
    console.log("context userID:", userID);
    console.log("effectiveUserId:", effectiveUserId);
    console.log("packageType:", packageType);
    console.log("duration:", duration);

    if (!effectiveUserId) {
      console.error("ERROR: No userId available!");
      throw new Error("Cannot update subscription: No user ID available");
    }

    const today = moment();

    // Fetch current userTypeChangeDate from database to get accurate remaining days
    // Don't rely on context daysLeft as it may be stale after redirect
    let currentDaysLeft = 0;
    try {
      console.log("Fetching user with query getUser(id:", effectiveUserId, ")");
      const currentUser = await API.graphql(
        graphqlOperation(`
          query {
            getUser(id: "${effectiveUserId}") {
              id
              userType
              userTypeChangeDate
            }
          }
        `)
      );
      console.log("Query response:", JSON.stringify(currentUser, null, 2));
      
      const userTypeChangeDate = currentUser.data?.getUser?.userTypeChangeDate;
      console.log("Current userTypeChangeDate from DB:", userTypeChangeDate);
      
      if (userTypeChangeDate) {
        const existingExpiryDate = moment(userTypeChangeDate, "MMMM DD, YYYY");
        currentDaysLeft = Math.max(existingExpiryDate.diff(moment(), "days"), 0);
        console.log("Parsed expiry date:", existingExpiryDate.format("MMMM DD, YYYY"));
        console.log("Remaining days (X):", currentDaysLeft);
      } else {
        console.log("No existing userTypeChangeDate, using 0 remaining days");
      }
    } catch (err) {
      console.error("Failed to fetch current subscription:", err);
    }

    // Calculate new expiry: today + remaining days + new subscription duration
    const newExpiryDate = today
      .clone()
      .add(currentDaysLeft + duration, "days")
      .format("MMMM DD, YYYY");
    console.log("=== EXPIRY CALCULATION ===");
    console.log("Today:", today.format("MMMM DD, YYYY"));
    console.log("Remaining days (X):", currentDaysLeft);
    console.log("Duration to add:", duration);
    console.log("Total days:", currentDaysLeft + duration);
    console.log("New expiry date:", newExpiryDate);

    const newExpiryMoment = moment(newExpiryDate, "MMMM DD, YYYY");
    const subscriptionDays = newExpiryMoment.diff(moment(), "days");

    try {
      const userType = packageType.toUpperCase(); // PRO or CORPORATE

      console.log("=== MUTATION ===");
      console.log("updateUser(input: { id:", effectiveUserId, ", userType:", userType, ", userTypeChangeDate:", newExpiryDate, "})");

      const updateUserResult = await API.graphql(
        graphqlOperation(`
          mutation {
            updateUser(input: {
              id: "${effectiveUserId}",
              userType: "${userType}",
              userTypeChangeDate: "${newExpiryDate}"
            }) {
              id
              userType
              userTypeChangeDate
            }
          }
        `)
      );

      console.log("Mutation response:", JSON.stringify(updateUserResult, null, 2));

      if (updateUserResult.data?.updateUser) {
        // Update local state
        setDaysLeft(subscriptionDays);
        setUserTypeAWS(userType);
        setUserTypeLocal(userType);
        console.log("✅ Subscription updated successfully!");
        console.log("New userTypeChangeDate in DB:", updateUserResult.data.updateUser.userTypeChangeDate);
      } else {
        console.error("Mutation returned no data");
      }
    } catch (err) {
      console.error("Failed to update subscription:", err);
      throw new Error(
        "Payment received but failed to activate subscription. Please contact support."
      );
    }
  };

  const renderContent = () => {
    switch (status) {
      case "checking":
        return (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={60} color="primary" />
            <Typography variant="h5" sx={{ mt: 3 }}>
              Verifying Payment...
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              Please wait while we confirm your payment with Pesapal
            </Typography>
            {errorMessage && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                {errorMessage}
              </Typography>
            )}
          </Box>
        );

      case "success":
        return (
          <Box sx={{ textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: "success.main" }} />
            <Typography variant="h4" sx={{ mt: 3, color: "success.main" }}>
              Payment Successful!
            </Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Your {paymentDetails?.packageType} subscription is now active
            </Typography>
            {paymentDetails && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1">
                  <strong>Package:</strong> {paymentDetails.packageType}
                </Typography>
                <Typography variant="body1">
                  <strong>Amount:</strong> {paymentDetails.currency}{" "}
                  {paymentDetails.amount}
                </Typography>
                <Typography variant="body1">
                  <strong>Duration:</strong> {paymentDetails.duration} days
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              color="success"
              size="large"
              sx={{ mt: 4 }}
              onClick={() => navigate("/")}
            >
              Go to Dashboard
            </Button>
          </Box>
        );

      case "failed":
        return (
          <Box sx={{ textAlign: "center" }}>
            <ErrorIcon sx={{ fontSize: 80, color: "error.main" }} />
            <Typography variant="h4" sx={{ mt: 3, color: "error.main" }}>
              Payment Failed
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {errorMessage || "Your payment could not be processed."}
            </Typography>
            <Box
              sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/pesapal")}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  window.open("https://www.loantabs.com/support", "_blank")
                }
              >
                Contact Support
              </Button>
            </Box>
          </Box>
        );

      case "error":
      default:
        return (
          <Box sx={{ textAlign: "center" }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage ||
                "An error occurred while processing your payment"}
            </Alert>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/pesapal")}
              >
                Back to Packages
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  window.open("https://www.loantabs.com/support", "_blank")
                }
              >
                Contact Support
              </Button>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        p: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: 500,
          width: "100%",
          p: 4,
          borderRadius: 3,
          bgcolor: "background.default",
          boxShadow: 3,
        }}
      >
        <Typography variant="h5" sx={{ mb: 4, textAlign: "center" }}>
          Loan<span style={{ color: "#70d8bd" }}>Tabs</span> Payment
        </Typography>
        {renderContent()}
      </Box>
    </Box>
  );
}

export default PesapalCallback;
